import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Minus, Zap } from "lucide-react";
import { useApp } from "../context/AppContext";
import { fetchEvents, buyTickets, stripeCheckoutTickets } from "../lib/api";
import { langField } from "../lib/i18n";

export default function Tickets() {
  const { t, lang, formatPrice } = useApp();
  const [searchParams] = useSearchParams();
  const wantedId = searchParams.get("event");
  const [events, setEvents] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({ customer_email: "", customer_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchEvents().then((evs) => {
      setEvents(evs);
      if (evs.length && !selectedId) {
        const initial = wantedId && evs.some((e) => e.id === wantedId) ? wantedId : evs[0].id;
        setSelectedId(initial);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedId]);

  const selected = events.find((e) => e.id === selectedId);
  const mode = selected?.ticket_mode || (selected?.is_free ? "free" : selected?.ticket_url ? "external" : "stripe");
  const pricePer = selected?.ticket_price != null ? Number(selected.ticket_price) : (selected?.type === "music" ? 18 : 25);
  const total = pricePer * qty;

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await stripeCheckoutTickets({
        event_id: selectedId,
        customer_email: form.customer_email,
        customer_name: form.customer_name,
        quantity: qty,
        origin_url: window.location.origin,
        language: lang,
      });
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      throw new Error("No checkout URL received");
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-[800px] mx-auto px-5 md:px-10 py-20 text-center" data-testid="tickets-success">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">⚡ TICKETS ISSUED</p>
        <h1 className="font-display text-6xl md:text-8xl mt-4 leading-none">{t.tickets.success}</h1>
        <p className="text-kult-muted mt-4 text-lg">{t.tickets.success_sub}</p>

        <div className="mt-10 border-2 border-kult-pink p-6 text-left kult-shadow-yellow">
          <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-3">TICKET CODE</p>
          <p className="font-display text-3xl break-all">{result.ticket_code}</p>
          <div className="border-t border-kult-border mt-4 pt-4 grid grid-cols-2 gap-3 font-mono text-sm">
            <div><span className="text-kult-muted">EVENT</span><br />{result.event_title}</div>
            <div><span className="text-kult-muted">VENUE</span><br />{result.event_venue}</div>
            <div><span className="text-kult-muted">QTY</span><br />×{result.quantity}</div>
            <div><span className="text-kult-muted">TOTAL</span><br />{formatPrice(result.total)}</div>
          </div>
        </div>

        <p className="font-mono text-xs text-kult-muted mt-4">{t.tickets.mock_disclaimer}</p>
        <Link to="/" className="inline-block mt-8 bg-kult-pink text-black px-8 py-4 font-display text-xl">
          {t.checkout.back_home}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20" data-testid="page-tickets">
      <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ TICKETS</p>
      <h1 className="font-display text-6xl md:text-9xl mt-2 mb-3 leading-none">
        {t.tickets.title.split(" ")[0]} <span className="text-kult-pink">{t.tickets.title.split(" ").slice(1).join(" ") || "."}</span>
      </h1>
      <p className="text-kult-muted max-w-xl mb-10">{t.tickets.sub}</p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-4">{t.tickets.pick_event}</p>
          <div className="space-y-3">
            {events.map((ev) => {
              const d = new Date(ev.date);
              const isSel = selectedId === ev.id;
              const evMode = ev.ticket_mode || (ev.is_free ? "free" : ev.ticket_url ? "external" : "stripe");
              const evPrice = ev.ticket_price != null ? Number(ev.ticket_price) : (ev.type === "music" ? 18 : 25);
              return (
                <button
                  key={ev.id}
                  type="button"
                  onClick={() => setSelectedId(ev.id)}
                  className={`w-full text-left grid grid-cols-12 gap-3 md:gap-5 p-4 md:p-5 border-2 transition-colors ${
                    isSel ? "border-kult-pink bg-kult-black2" : "border-kult-border hover:border-white"
                  }`}
                  data-testid={`ticket-event-${ev.id}`}
                >
                  <span className="col-span-3 md:col-span-2 aspect-square overflow-hidden bg-black">
                    <img src={ev.image} alt="" className="w-full h-full object-cover grayscale" />
                  </span>
                  <span className="col-span-9 md:col-span-10 flex flex-col justify-center">
                    <span className="font-mono text-[10px] tracking-[0.25em] text-kult-yellow">
                      {ev.type === "wrestling" ? t.tickets.wrestling_price : t.tickets.music_price}
                      {" · "}
                      {evMode === "free"
                        ? (lang === "pt" ? "GRÁTIS" : "FREE")
                        : formatPrice(evPrice, { decimals: evPrice % 1 === 0 ? 0 : 2 })}
                    </span>
                    <span className="font-display text-2xl md:text-3xl mt-1">{langField(ev, "title", lang)}</span>
                    <span className="font-mono text-xs tracking-[0.2em] text-kult-muted mt-1">
                      {d.toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", {
                        weekday: "short",
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      }).toUpperCase()}
                      {" · "}
                      {ev.city.toUpperCase()} / {ev.venue}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="sticky top-24 border-2 border-kult-border p-6 bg-kult-black2 space-y-5" data-testid="tickets-side-panel">
            {!selected ? (
              <p className="font-mono text-xs text-kult-muted">{lang === "pt" ? "Seleciona um evento à esquerda." : "Pick an event on the left."}</p>
            ) : mode === "free" ? (
              <>
                <h3 className="font-display text-3xl">{lang === "pt" ? "ENTRADA LIVRE" : "FREE ENTRY"}</h3>
                <p className="text-kult-muted text-sm">
                  {lang === "pt"
                    ? "Sem bilhete necessário. Aparece e divirte-te."
                    : "No ticket required. Just show up."}
                </p>
                <div className="border-t border-kult-border pt-4">
                  <p className="font-display text-3xl bg-kult-yellow text-black inline-block px-3 py-1" data-testid="ticket-free-badge">
                    {lang === "pt" ? "GRÁTIS" : "FREE"}
                  </p>
                </div>
              </>
            ) : mode === "external" ? (
              <>
                <h3 className="font-display text-3xl">{lang === "pt" ? "COMPRA EXTERNA" : "EXTERNAL TICKETS"}</h3>
                <p className="text-kult-muted text-sm">
                  {lang === "pt"
                    ? "Os bilhetes deste evento são vendidos por um parceiro externo. Clica abaixo para comprar."
                    : "Tickets for this event are sold by a partner. Click below to buy."}
                </p>
                <div className="border-t border-kult-border pt-4">
                  {selected.ticket_price != null && (
                    <p className="font-mono text-xs tracking-[0.25em] text-kult-muted mb-2">
                      {lang === "pt" ? "DESDE" : "FROM"} <span className="font-display text-xl text-white">{formatPrice(Number(selected.ticket_price))}</span>
                    </p>
                  )}
                  <a
                    href={selected.ticket_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-kult-pink text-black font-display text-2xl py-4 hover:bg-kult-yellow transition-colors inline-flex items-center justify-center gap-2"
                    data-testid="ticket-external-link"
                  >
                    <Zap size={20} /> {lang === "pt" ? "COMPRAR BILHETES" : "BUY TICKETS"} ↗
                  </a>
                </div>
              </>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="tickets-form">
                <h3 className="font-display text-3xl">CHECKOUT</h3>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">{t.tickets.qty}</p>
                  <div className="flex items-center border border-kult-border w-fit">
                    <button
                      type="button"
                      onClick={() => setQty(Math.max(1, qty - 1))}
                      className="p-3 hover:bg-kult-pink hover:text-black"
                      data-testid="ticket-qty-minus"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="px-5 font-display text-2xl min-w-[60px] text-center" data-testid="ticket-qty">{qty}</span>
                    <button
                      type="button"
                      onClick={() => setQty(Math.min(10, qty + 1))}
                      className="p-3 hover:bg-kult-pink hover:text-black"
                      data-testid="ticket-qty-plus"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>

                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">{t.checkout.name}</p>
                  <input
                    required
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
                    data-testid="ticket-name"
                  />
                </div>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">{t.checkout.email}</p>
                  <input
                    type="email"
                    required
                    value={form.customer_email}
                    onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                    className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
                    data-testid="ticket-email"
                  />
                </div>

                <div className="border-t border-kult-border pt-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-xs tracking-[0.25em] text-kult-muted">{t.tickets.price_per}</span>
                    <span className="font-mono text-sm">{formatPrice(pricePer)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs tracking-[0.25em]">{t.tickets.total}</span>
                    <span className="font-display text-3xl bg-kult-yellow text-black px-2" data-testid="ticket-total">
                      {formatPrice(total)}
                    </span>
                  </div>
                </div>

                {error && <p className="font-mono text-xs text-kult-pink">{error}</p>}

                <button
                  type="submit"
                  disabled={!selectedId || submitting}
                  className="w-full bg-kult-pink text-black font-display text-2xl py-4 hover:bg-kult-yellow transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
                  data-testid="ticket-submit"
                >
                  <Zap size={20} /> {submitting ? "..." : t.tickets.checkout}
                </button>
                <p className="font-mono text-[10px] tracking-[0.2em] text-kult-muted text-center">
                  {t.tickets.mock_disclaimer}
                </p>
              </form>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
